import {ReactNode, useEffect, useState} from 'react';
import {useQueryClient} from 'react-query';
import {DateTime} from 'luxon';
import {useIntl} from 'react-intl';
import {
  Alert,
  Button as BootstrapButton,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import Button from '../../../../../Components/Button';
import Loading from '../../../../../app/common/components/Loading';
import {
  useGetPbxGroupes,
  useGetPlagesHoraires,
  useGetVirtualPbxActivite,
  useGetVirtualPbxNumeros,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import {
  ID_PLAGE_HORS_PLAGE,
  ID_PLAGE_VACANCES,
  JourSemaine,
  NumeroGlobal,
  PlageAssocieeNumero,
  PlageHoraireDetail,
  PlageHoraireGroupe,
  TypeGroupePlageNumero,
} from '../TrunkSip/mocks';
import AppelsSortants from './AppelsSortants';
import BoiteVocale from './BoiteVocale';
import CallFlowVisualizer from './CallFlowVisualizer';
import ConferenceRoom from './ConferenceRoom';
import ConfigurationSvi from './ConfigurationSvi';
import GererTeams from './GererTeams';
import GestionSons from './GestionSons';
import HistoriqueAppels from './HistoriqueAppels';
import PlagesHoraires, {JoursChips, TimelinePlage} from './PlagesHoraires';
import Groupes, {DEVIER_VERS_LABEL_ID, TYPE_DISTRIBUTION_LABEL_ID} from './Groupes';

const ACTION_SAISIR_NUMERO = 'Saisir un numéro';

const ACTION_CREER_GROUPE = 'Créer un groupe';

const DESTINATIONS_TRANSFERT_ACTIONS = [ACTION_SAISIR_NUMERO, ACTION_CREER_GROUPE];

const GROUPE_PLAGE_VACANCES = 'Vacances';

const GROUPE_PLAGE_HORS_PLAGE = 'Hors plage horaire';

const GROUPE_PLAGE_ACTION = 'Ajouter un groupe de plage horaire';

interface PbxSousOnglet {
  key: string;
  labelId: string;
  icon: string;
}

/**
 * Les 9 onglets réels de la section « Gérer votre Virtual PBX » (barre vtx-tabs du
 * legacy) — remplace une précédente liste construite avant d'avoir l'analyse complète.
 * « historique » (Symphonie VOIP — Historique des appels) ajouté en plus à la demande
 * explicite, hors des 9 onglets d'origine de cette barre.
 */
const PBX_SOUS_ONGLETS: PbxSousOnglet[] = [
  {key: 'numeros', labelId: 'TELEPHONIE.PBX.SOUSONGLET.NUMEROS', icon: 'bi-telephone'},
  {key: 'plages', labelId: 'TELEPHONIE.PBX.SOUSONGLET.PLAGES', icon: 'bi-clock'},
  {key: 'groupes', labelId: 'TELEPHONIE.PBX.SOUSONGLET.GROUPES', icon: 'bi-people'},
  {key: 'bv', labelId: 'TELEPHONIE.PBX.SOUSONGLET.BV', icon: 'bi-voicemail'},
  {key: 'sortants', labelId: 'TELEPHONIE.PBX.SOUSONGLET.SORTANTS', icon: 'bi-telephone-outbound'},
  {key: 'melodies', labelId: 'TELEPHONIE.PBX.SOUSONGLET.MELODIES', icon: 'bi-music-note-beamed'},
  {key: 'annonces', labelId: 'TELEPHONIE.PBX.SOUSONGLET.ANNONCES', icon: 'bi-megaphone'},
  {key: 'ivr', labelId: 'TELEPHONIE.PBX.SOUSONGLET.IVR', icon: 'bi-diagram-3'},
  {key: 'conference', labelId: 'TELEPHONIE.PBX.SOUSONGLET.CONFERENCE', icon: 'bi-camera-video'},
  {key: 'historique', labelId: 'TELEPHONIE.PBX.SOUSONGLET.HISTORIQUE', icon: 'bi-clock-history'},
];

const regleVersPlageAssociee = (
  regle: {groupePlage: string; destinationTransfert: string},
  index: number
): PlageAssocieeNumero => {
  const idPlage =
    regle.groupePlage === GROUPE_PLAGE_HORS_PLAGE
      ? ID_PLAGE_HORS_PLAGE
      : regle.groupePlage === GROUPE_PLAGE_VACANCES
        ? ID_PLAGE_VACANCES
        : index + 1;
  const typeGroupe: TypeGroupePlageNumero = regle.destinationTransfert
    .toLowerCase()
    .startsWith('boîte vocale')
    ? 'systeme'
    : 'distribution';
  return {
    idPlage,
    nomPlage: regle.groupePlage,
    typeGroupe,
    nomGroupe: regle.destinationTransfert,
  };
};

const plageAssocieeVersRegle = (
  plage: PlageAssocieeNumero
): {groupePlage: string; destinationTransfert: string} => ({
  groupePlage:
    plage.idPlage === ID_PLAGE_HORS_PLAGE
      ? GROUPE_PLAGE_HORS_PLAGE
      : plage.idPlage === ID_PLAGE_VACANCES
        ? GROUPE_PLAGE_VACANCES
        : plage.nomPlage,
  destinationTransfert: plage.nomGroupe,
});

const VueNumerosConfig = ({
  numeroCible,
  numeroInitial,
  numerosExistants,
  onFermer,
  onCreerGroupe,
  onAjouterGroupePlage,
  onEnregistrerNumero,
}: {
  numeroCible: NumeroGlobal | null;
  numeroInitial?: string;
  numerosExistants: NumeroGlobal[];
  onFermer: () => void;
  onCreerGroupe: () => void;
  onAjouterGroupePlage: () => void;
  onEnregistrerNumero: (numero: Omit<NumeroGlobal, 'id'> & {id?: number}) => void;
}): JSX.Element | null => {
  const intl = useIntl();
  const {data: activiteData, isLoading: isLoadingActivite} = useGetVirtualPbxActivite();
  const {data: plagesData, isLoading: isLoadingPlages} = useGetPlagesHoraires();
  const {data: pbxGroupesData} = useGetPbxGroupes();
  const [numero, setNumero] = useState('');
  const [intitule, setIntitule] = useState('');
  const [rejetAppelsAnonymes, setRejetAppelsAnonymes] = useState(false);
  const [groupePlage, setGroupePlage] = useState('');
  const [destinationTransfert, setDestinationTransfert] = useState('');
  const [numeroSaisi, setNumeroSaisi] = useState('');
  const [regles, setRegles] = useState<{groupePlage: string; destinationTransfert: string}[]>([]);
  const [regleASupprimer, setRegleASupprimer] = useState<number | null>(null);

  useEffect(() => {
    if (numeroCible) {
      setNumero(numeroCible.numeroFormate);
      setIntitule(numeroCible.nom);
      setRejetAppelsAnonymes(numeroCible.rejetAppelsAnonymes);
      setRegles(numeroCible.plages.map(plageAssocieeVersRegle));
    } else if (numeroInitial) {
      setNumero(numeroInitial);
      setIntitule('');
      setRejetAppelsAnonymes(false);
      setRegles([]);
    } else if (activiteData) {
      const dejaUtilises = new Set(numerosExistants.map((n) => n.numeroFormate));
      const toutesOptions = [activiteData.numeroGlobal, ...activiteData.autresNumerosAttribues];
      const premierDisponible =
        toutesOptions.find((n) => !dejaUtilises.has(n)) ?? activiteData.numeroGlobal;
      setNumero(premierDisponible);
      setIntitule('');
      setRejetAppelsAnonymes(false);
      setRegles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeroCible, numeroInitial, activiteData]);

  if (isLoadingActivite || !activiteData || isLoadingPlages || !plagesData) {
    return <Loading className="col-md-6" />;
  }

  const numerosDejaUtilises = new Set(
    numerosExistants.filter((n) => n.id !== numeroCible?.id).map((n) => n.numeroFormate)
  );
  const optionsNumero = [activiteData.numeroGlobal, ...activiteData.autresNumerosAttribues].filter(
    (n) => !numerosDejaUtilises.has(n)
  );

  const ajouterRegle = (): void => {
    const destination =
      destinationTransfert === ACTION_SAISIR_NUMERO ? numeroSaisi.trim() : destinationTransfert;
    if (destination === '') {
      return;
    }
    setRegles([...regles, {groupePlage, destinationTransfert: destination}]);
    setGroupePlage('');
    setDestinationTransfert('');
    setNumeroSaisi('');
  };

  const confirmerSuppressionRegle = (): void => {
    if (regleASupprimer != null) {
      setRegles(regles.filter((_, i) => i !== regleASupprimer));
    }
    setRegleASupprimer(null);
  };

  return (
    <div>
      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold fs-6 mb-2 text-uppercase">
            <i className="bi bi-telephone-fill me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.TITRE'})}
          </div>
          <p className="fs-6 mb-4">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.DESCRIPTION'})}
          </p>

          <Form.Label className="fw-bold fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.NUMERO'})}
          </Form.Label>
          <Form.Select
            style={{maxWidth: 200}}
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
          >
            {optionsNumero.map((n) => (
              <option key={n} value={n}>
                {n.replace(/\s/g, '')}
              </option>
            ))}
          </Form.Select>

          <Form.Label className="fw-bold fs-6 mt-4">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.INTITULE'})}
          </Form.Label>
          <Form.Control
            style={{maxWidth: 320}}
            value={intitule}
            onChange={(e) => setIntitule(e.target.value)}
          />

          <Form.Check
            className="mt-4"
            type="checkbox"
            id="rejet-appels-anonymes"
            checked={rejetAppelsAnonymes}
            onChange={(e) => setRejetAppelsAnonymes(e.target.checked)}
            label={
              <span>
                {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.REJET_ANONYMES'})}{' '}
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      {intl.formatMessage({
                        id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.REJET_ANONYMES_TOOLTIP',
                      })}
                    </Tooltip>
                  }
                >
                  <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
                </OverlayTrigger>
              </span>
            }
          />
        </Card.Body>
      </Card>

      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold fs-6 mb-4 text-uppercase">
            <i className="bi bi-clock-fill me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.PLAGES_TITRE'})}
          </div>

          {regles.length > 0 && (
            <table className="table table-sm mb-4">
              <thead>
                <tr className="table-primary">
                  <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.GROUPE_PLAGE'})}</th>
                  <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.DESTINATION'})}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {regles.map((regle, index) => (
                  <tr key={index}>
                    <td>
                      <i
                        className={`bi ${
                          regle.groupePlage === GROUPE_PLAGE_HORS_PLAGE
                            ? 'bi-circle-half'
                            : 'bi-list-ul'
                        } text-primary me-2`}
                      />
                      {regle.groupePlage}
                    </td>
                    <td>{regle.destinationTransfert}</td>
                    <td className="text-end">
                      <i
                        className="bi bi-trash text-danger cursor-pointer"
                        onClick={() => setRegleASupprimer(index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Row className="g-4 align-items-end">
            <Col xs={12} md={5}>
              <Form.Label className="fw-bold fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.GROUPE_PLAGE'})}
              </Form.Label>
              <Form.Select
                value={groupePlage}
                onChange={(e) => {
                  const valeur = e.target.value;
                  if (valeur === GROUPE_PLAGE_ACTION) {
                    onAjouterGroupePlage();
                    return;
                  }
                  setGroupePlage(valeur);
                }}
              >
                <option value="">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.CHOISIR_GROUPE_PLAGE'})}
                </option>
                {plagesData.groupes.map((groupe) => (
                  <option key={groupe.id} value={groupe.nom}>
                    {groupe.nom}
                  </option>
                ))}
                <option value={GROUPE_PLAGE_VACANCES}>{GROUPE_PLAGE_VACANCES}</option>
                <option value={GROUPE_PLAGE_HORS_PLAGE}>{GROUPE_PLAGE_HORS_PLAGE}</option>
                <option value={GROUPE_PLAGE_ACTION} style={{fontWeight: 'bold'}}>
                  {GROUPE_PLAGE_ACTION}
                </option>
              </Form.Select>
            </Col>
            <Col xs={12} md={5}>
              <Form.Label className="fw-bold fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.DESTINATION'})}
              </Form.Label>
              <Form.Select
                value={destinationTransfert}
                onChange={(e) => {
                  const valeur = e.target.value;
                  if (valeur === ACTION_CREER_GROUPE) {
                    onCreerGroupe();
                    return;
                  }
                  setDestinationTransfert(valeur);
                }}
              >
                <option value="">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.CHOISIR_DESTINATION'})}
                </option>
                {(pbxGroupesData?.groupes ?? []).map((groupe) => (
                  <option key={`groupe-${groupe.id}`} value={groupe.nom}>
                    {groupe.nom}
                  </option>
                ))}
                {DESTINATIONS_TRANSFERT_ACTIONS.map((action) => (
                  <option key={action} value={action} style={{fontWeight: 'bold'}}>
                    {action}
                  </option>
                ))}
              </Form.Select>
              {destinationTransfert === ACTION_SAISIR_NUMERO && (
                <>
                  <div className="d-flex align-items-center gap-3 bg-primary text-white rounded p-3 mt-2">
                    <i className="bi bi-exclamation-triangle-fill fs-4 flex-shrink-0" />
                    <span className="fs-7">
                      {intl.formatMessage(
                        {id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.SAISIR_NUMERO_INFO'},
                        {b: (chunks: ReactNode[]) => <strong>{chunks}</strong>}
                      )}
                    </span>
                  </div>
                  <Form.Control
                    className="mt-2"
                    value={numeroSaisi}
                    onChange={(e) => setNumeroSaisi(e.target.value)}
                  />
                  <Form.Text className="text-primary fst-italic">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.SAISIR_NUMERO_EXEMPLE'})}
                  </Form.Text>
                </>
              )}
            </Col>
            <Col xs={12} md={2}>
              <BootstrapButton variant="primary" onClick={ajouterRegle} className="w-100">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.AJOUTER'})}
              </BootstrapButton>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-center gap-3">
        <BootstrapButton variant="secondary" onClick={onFermer}>
          {intl.formatMessage({id: 'CANCEL'})}
        </BootstrapButton>
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
          disabled={intitule.trim() === ''}
          onClick={() => {
            onEnregistrerNumero({
              id: numeroCible?.id,
              numero: numero.replace(/\s/g, ''),
              numeroFormate: numero,
              nom: intitule.trim(),
              rejetAppelsAnonymes,
              plages: regles.map(regleVersPlageAssociee),
            });
            onFermer();
          }}
        />
      </div>

      <Modal show={regleASupprimer != null} onHide={() => setRegleASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.DELETE_REGLE_BODY'},
            {
              destination:
                regleASupprimer != null ? regles[regleASupprimer]?.destinationTransfert ?? '' : '',
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setRegleASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppressionRegle}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const TYPE_GROUPE_ICON: Record<TypeGroupePlageNumero, string> = {
  distribution: 'bi-people-fill',
  menu: 'bi-diagram-3-fill',
  poste: 'bi-telephone-fill',
  systeme: 'bi-gear-fill',
};

const GROUPE_SYSTEME_PREFIXES = ['DA ', 'VM ', 'FWD '];

const estGroupeSysteme = (nomGroupe: string): boolean =>
  GROUPE_SYSTEME_PREFIXES.some((prefixe) => nomGroupe.startsWith(prefixe));

const VueNumeros = ({
  modeConfig,
  numeroAConfigurer,
  onFermerConfig,
  onCreerGroupe,
  onAjouterGroupePlage,
  onOuvrirConfig,
}: {
  modeConfig: boolean;
  numeroAConfigurer?: string;
  onFermerConfig: () => void;
  onCreerGroupe: () => void;
  onAjouterGroupePlage: () => void;
  onOuvrirConfig: () => void;
}): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data, isLoading} = useGetVirtualPbxNumeros();
  const {data: plagesData} = useGetPlagesHoraires();
  const [numerosGlobauxLocal, setNumerosGlobauxLocal] = useState<NumeroGlobal[] | null>(null);
  const [numeroASupprimer, setNumeroASupprimer] = useState<NumeroGlobal | null>(null);
  const [numeroCible, setNumeroCible] = useState<NumeroGlobal | null>(null);
  const [detailsOuverts, setDetailsOuverts] = useState<Set<string>>(new Set());
  const [message, setMessage] = useAutoDismissMessage();

  useEffect(() => {
    if (data) {
      setNumerosGlobauxLocal(data.numerosGlobaux);
    }
  }, [data]);

  const enregistrerNumero = (payload: Omit<NumeroGlobal, 'id'> & {id?: number}): void => {
    const actuels = numerosGlobauxLocal ?? [];
    const misAJour =
      payload.id != null
        ? actuels.map((numero) =>
            numero.id === payload.id ? {...payload, id: payload.id as number} : numero
          )
        : [...actuels, {...payload, id: Math.max(0, ...actuels.map((n) => n.id)) + 1}];
    setNumerosGlobauxLocal(misAJour);
    queryClient.setQueryData('centrex-pbx-numeros', {numerosGlobaux: misAJour});
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  if (modeConfig) {
    return (
      <VueNumerosConfig
        numeroCible={numeroCible}
        numeroInitial={numeroCible ? undefined : numeroAConfigurer}
        numerosExistants={numerosGlobauxLocal ?? []}
        onFermer={onFermerConfig}
        onCreerGroupe={onCreerGroupe}
        onAjouterGroupePlage={onAjouterGroupePlage}
        onEnregistrerNumero={enregistrerNumero}
      />
    );
  }

  if (isLoading || !data || !numerosGlobauxLocal || !plagesData) {
    return <Loading className="col-md-6" />;
  }

  const numerosGlobaux = numerosGlobauxLocal;

  const basculerDetails = (cle: string): void => {
    setDetailsOuverts((precedent) => {
      const suivant = new Set(precedent);
      if (suivant.has(cle)) {
        suivant.delete(cle);
      } else {
        suivant.add(cle);
      }
      return suivant;
    });
  };

  const confirmerSuppressionNumero = (): void => {
    if (numeroASupprimer) {
      const misAJour = numerosGlobaux.filter((numero) => numero.id !== numeroASupprimer.id);
      setNumerosGlobauxLocal(misAJour);
      queryClient.setQueryData('centrex-pbx-numeros', {numerosGlobaux: misAJour});
      setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
    }
    setNumeroASupprimer(null);
  };

  return (
    <div>
      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold fs-6 mb-2 text-uppercase">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.NUMEROS_GLOBAUX'})}
          </div>
          <p className="text-muted fs-6 mb-0">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.DESCRIPTION'})}
          </p>
        </Card.Body>
      </Card>

      {message && <div className="fw-bold text-danger mb-3">{message}</div>}

      {numerosGlobaux.length === 0 && (
        <p className="text-muted fs-6">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.AUCUN_NUMERO_GLOBAL'})}
        </p>
      )}

      {numerosGlobaux.map((numero) => {
        const aHorsPlage = numero.plages.some((plage) => plage.idPlage === ID_PLAGE_HORS_PLAGE);
        return (
          <Card key={numero.id} className="mb-4">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-telephone-fill text-primary" />
                  <span className="fw-bold fs-6">{numero.numeroFormate}</span>
                  <span className="text-muted fs-6">({numero.nom})</span>
                  {numero.rejetAppelsAnonymes && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          {intl.formatMessage({
                            id: 'TELEPHONIE.PBX.NUMEROS.CONFIG.REJET_ANONYMES',
                          })}
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-incognito text-secondary" />
                    </OverlayTrigger>
                  )}
                </div>
                <div className="d-flex align-items-center gap-3">
                  <Button
                    label={intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.CONFIGURER'})}
                    onClick={() => {
                      setNumeroCible(numero);
                      onOuvrirConfig();
                    }}
                  />
                  <i
                    className="bi bi-printer fs-4 text-primary cursor-pointer"
                    onClick={() => window.print()}
                  />
                  <i
                    className="bi bi-trash fs-4 text-danger cursor-pointer"
                    onClick={() => setNumeroASupprimer(numero)}
                  />
                </div>
              </div>

              {!aHorsPlage && (
                <Alert variant="warning" className="d-flex align-items-center mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-3" />
                  <span className="fs-7">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.PAS_DE_HORS_PLAGE'})}
                  </span>
                </Alert>
              )}

              {numero.plages.map((plage) => {
                const cle = `${numero.id}-${plage.idPlage}`;
                const masquerDetails = estGroupeSysteme(plage.nomGroupe);
                const planningPlage = plagesData.groupes.find(
                  (groupe) => groupe.nom === plage.nomPlage
                );
                return (
                  <div key={cle} className="border rounded p-3 mb-2 bg-light">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${TYPE_GROUPE_ICON[plage.typeGroupe]} text-primary`} />
                        <span className="fw-semibold fs-7">{plage.nomPlage}</span>
                      </div>
                      {!masquerDetails && (
                        <BootstrapButton
                          size="sm"
                          variant="outline-primary"
                          onClick={() => basculerDetails(cle)}
                        >
                          {intl.formatMessage({
                            id: detailsOuverts.has(cle)
                              ? 'TELEPHONIE.PBX.NUMEROS.FERMER'
                              : 'TELEPHONIE.PBX.NUMEROS.DETAILS',
                          })}
                        </BootstrapButton>
                      )}
                    </div>
                    <div className="text-muted fs-8 mt-1">
                      {intl.formatMessage(
                        {id: 'TELEPHONIE.PBX.NUMEROS.PRISE_EN_CHARGE'},
                        {groupe: plage.nomGroupe}
                      )}
                      {plage.alias && ` — ${plage.alias}`}
                    </div>
                    {!masquerDetails && detailsOuverts.has(cle) && (
                      <div className="bg-white rounded p-3 mt-2 fs-7">
                        <div>
                          <strong>
                            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.DETAIL_PLAGE'})}
                          </strong>{' '}
                          {plage.nomPlage}
                        </div>
                        {planningPlage &&
                          planningPlage.plages.map((detailHoraire) => (
                            <div key={detailHoraire.id} className="mt-2">
                              {detailHoraire.periodicite === 'semaine' && (
                                <JoursChips jours={detailHoraire.jours ?? []} />
                              )}
                              <div className="mt-1">
                                <TimelinePlage periodes={detailHoraire.periodes} />
                              </div>
                            </div>
                          ))}
                        <div className="mt-3">
                          <strong>
                            {intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.DETAIL_GROUPE'})}
                          </strong>{' '}
                          {plage.nomGroupe}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        );
      })}

      <div className="d-flex flex-wrap justify-content-between gap-3 mt-4">
        {numerosGlobaux.length > 0 && (
          <i
            className="bi bi-printer fs-3 text-primary cursor-pointer"
            onClick={() => window.print()}
          />
        )}
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.PBX.NUMEROS.ADD'})}
          faIcon="bi bi-plus-lg fs-6 me-2"
          onClick={() => {
            setNumeroCible(null);
            onOuvrirConfig();
          }}
        />
      </div>

      <Modal show={numeroASupprimer != null} onHide={() => setNumeroASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.NUMEROS.DELETE_BODY'},
            {
              numero: numeroASupprimer?.numeroFormate ?? '',
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setNumeroASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppressionNumero}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const JOURS_ORDRE_ACTIVITE: JourSemaine[] = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

const versMinutes = (heure: string): number => {
  const [h, m] = heure.split(':').map(Number);
  return h * 60 + (m || 0);
};

const estPlageHoraireActiveMaintenant = (plage: PlageHoraireDetail, maintenant: DateTime): boolean => {
  const depuis = DateTime.fromISO(plage.depuis);
  const finLe = plage.finLe ? DateTime.fromISO(plage.finLe) : null;
  if (depuis > maintenant) {
    return false;
  }
  if (finLe && finLe < maintenant) {
    return false;
  }
  if (plage.periodicite === 'semaine' && plage.jours) {
    const jourActuel = JOURS_ORDRE_ACTIVITE[maintenant.weekday - 1];
    if (!plage.jours.includes(jourActuel)) {
      return false;
    }
  }
  const minutesActuelles = maintenant.hour * 60 + maintenant.minute;
  return plage.periodes.some((periode) => {
    const debut = versMinutes(periode.heureDebut);
    const fin = versMinutes(periode.heureFin);
    return minutesActuelles >= debut && minutesActuelles <= fin;
  });
};

/**
 * Le legacy calcule la plage active « maintenant » (VOIP_ROUTING_PLAGES_HORAIRES_GET_NOW),
 * pas juste la liste de toutes les plages associées au numéro (comme dans le formulaire de
 * config) — on cherche donc la première plage planifiée dont l'horaire couvre l'instant présent,
 * et on retombe sur « Hors plage horaire » si aucune ne correspond.
 */
interface PlageActiveMaintenant {
  association: PlageAssocieeNumero;
  detail?: PlageHoraireDetail;
}

const trouverPlageActiveMaintenant = (
  numero: NumeroGlobal,
  groupesPlagesHoraires: PlageHoraireGroupe[],
  maintenant: DateTime
): PlageActiveMaintenant | undefined => {
  for (const association of numero.plages) {
    if (association.idPlage === ID_PLAGE_VACANCES || association.idPlage === ID_PLAGE_HORS_PLAGE) {
      continue;
    }
    const groupe = groupesPlagesHoraires.find((candidate) => candidate.nom === association.nomPlage);
    const detail = groupe?.plages.find((candidat) => estPlageHoraireActiveMaintenant(candidat, maintenant));
    if (detail) {
      return {association, detail};
    }
  }
  const horsPlage = numero.plages.find((plage) => plage.idPlage === ID_PLAGE_HORS_PLAGE);
  return horsPlage ? {association: horsPlage} : undefined;
};

const MoniteurActivite = ({
  onConfigurerNumeroGlobal,
  onOuvrirGestionTeams,
  onEditerGroupe,
}: {
  onConfigurerNumeroGlobal: (numero?: string) => void;
  onOuvrirGestionTeams: () => void;
  onEditerGroupe: (nomGroupe: string) => void;
}): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data, isLoading, refetch} = useGetVirtualPbxActivite();
  const {data: numerosData} = useGetVirtualPbxNumeros();
  const {data: plagesData} = useGetPlagesHoraires();
  const {data: pbxGroupesData} = useGetPbxGroupes();
  const [titre, setTitre] = useState('');
  const [modeEditionTitre, setModeEditionTitre] = useState(false);
  const [titreEdite, setTitreEdite] = useState('');
  const [headnumberSelectionne, setHeadnumberSelectionne] = useState<number | null>(null);
  const [detailsOuverts, setDetailsOuverts] = useState(false);

  useEffect(() => {
    if (data) {
      setTitre(data.titre);
    }
  }, [data]);

  useEffect(() => {
    if (numerosData && numerosData.numerosGlobaux.length > 0 && headnumberSelectionne == null) {
      setHeadnumberSelectionne(numerosData.numerosGlobaux[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numerosData]);

  if (isLoading || !data || !numerosData || !plagesData || !pbxGroupesData) {
    return <Loading className="col-md-6" />;
  }

  const dateActuelle = DateTime.now().setLocale('fr').toFormat("cccc d MMMM yyyy HH'h'mm");
  const nbDeconnectes = data.lignes.filter((ligne) => !ligne.connecte).length;
  const nbIndisponibles = data.lignes.filter((ligne) => !ligne.disponible).length;

  const basculerConnexion = (index: number): void => {
    const lignes = data.lignes.map((ligne, i) =>
      i === index ? {...ligne, connecte: !ligne.connecte} : ligne
    );
    queryClient.setQueryData('centrex-pbx-activite', {...data, lignes});
  };

  const numerosNonConfigures = data.autresNumerosAttribues.filter(
    (numero) => !numerosData.numerosGlobaux.some((global) => global.numero === numero.replace(/\s/g, ''))
  );

  const numeroActuel =
    numerosData.numerosGlobaux.find((numero) => numero.id === headnumberSelectionne) ?? null;
  const plageActiveInfo = numeroActuel
    ? trouverPlageActiveMaintenant(numeroActuel, plagesData.groupes, DateTime.now())
    : undefined;
  const plageActive = plageActiveInfo?.association;
  const masquerDetails = plageActive ? estGroupeSysteme(plageActive.nomGroupe) : false;
  const groupeActif =
    plageActive && !masquerDetails
      ? pbxGroupesData.groupes.find((groupe) => groupe.nom === plageActive.nomGroupe)
      : undefined;

  return (
    <Card bg="light">
      <Card.Body>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
          <div className="d-flex align-items-center gap-3">
            <span className="fw-bold fs-6 text-uppercase">
              {intl.formatMessage(
                {id: 'TELEPHONIE.PBX.MONITEUR.TITRE'},
                {prestation: data.prestationId}
              )}
            </span>
            <span>-</span>
            {modeEditionTitre ? (
              <>
                <Form.Control
                  size="sm"
                  style={{maxWidth: 260}}
                  value={titreEdite}
                  onChange={(e) => setTitreEdite(e.target.value)}
                />
                <i
                  className="bi bi-check-circle-fill text-success fs-4 cursor-pointer"
                  onClick={() => {
                    setTitre(titreEdite);
                    setModeEditionTitre(false);
                  }}
                />
              </>
            ) : (
              <>
                <span className="fs-6">{titre}</span>
                <i
                  className="bi bi-pencil-fill text-primary fs-5 cursor-pointer"
                  onClick={() => {
                    setTitreEdite(titre);
                    setModeEditionTitre(true);
                  }}
                />
              </>
            )}
          </div>
          <i className="bi bi-printer fs-3 text-primary cursor-pointer" onClick={() => window.print()} />
        </div>

        {data.teamsAssocie && (
          <div className="fs-6 mb-2">
            {intl.formatMessage(
              {id: 'TELEPHONIE.PBX.MONITEUR.TEAMS_VIRTUAL'},
              {prestation: data.teamsAssocie.prestationId, domaine: data.teamsAssocie.domaine}
            )}{' '}
            <i
              className="bi bi-gear-fill text-primary cursor-pointer"
              onClick={onOuvrirGestionTeams}
            />
          </div>
        )}

        <div className="d-flex align-items-center gap-2 mb-4">
          <span className="text-primary fw-semibold fs-6">
            {dateActuelle.charAt(0).toUpperCase() + dateActuelle.slice(1)}
          </span>
          <i
            className="bi bi-arrow-clockwise text-primary cursor-pointer"
            onClick={() => refetch()}
          />
        </div>

        <div className="fs-6 mb-1">
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.MONITEUR.UTILISATEURS_DECONNECTES'},
            {count: nbDeconnectes, b: (chunks: ReactNode[]) => <strong>{chunks}</strong>}
          )}
        </div>
        <div className="fs-6 mb-4">
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.MONITEUR.TELEPHONES_INDISPONIBLES'},
            {count: nbIndisponibles, b: (chunks: ReactNode[]) => <strong>{chunks}</strong>}
          )}
        </div>

        <div className="tbl-wrap mb-5" style={{maxHeight: 240, overflowY: 'auto'}}>
          <table className="table table-sm mb-0">
            <thead>
              <tr className="table-primary">
                <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.TABLE.UTILISATEUR'})}</th>
                <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.TABLE.TELEPHONE'})}</th>
                <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.TABLE.GROUPE'})}</th>
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((ligne, index) => (
                <tr key={`${ligne.telephone}-${index}`}>
                  <td>
                    <i
                      className={`bi bi-box-arrow-in-right cursor-pointer me-2 ${
                        ligne.connecte ? 'text-success' : 'text-danger'
                      }`}
                      onClick={() => basculerConnexion(index)}
                    />
                    {ligne.utilisateur}
                  </td>
                  <td>
                    <i
                      className={`bi bi-phone-fill me-2 ${
                        ligne.disponible ? 'text-success' : 'text-danger'
                      }`}
                    />
                    {ligne.telephone}
                  </td>
                  <td>{ligne.groupe}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Row className="g-4">
          <Col xs={12} md={6}>
            <div className="fw-semibold fs-6 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.PLAGE_ACTUELLE'})}
            </div>
            {numerosData.numerosGlobaux.map((numero) => (
              <Form.Check
                key={numero.id}
                className="mb-2"
                type="radio"
                id={`headnumber-${numero.id}`}
                name="headnumber"
                checked={headnumberSelectionne === numero.id}
                onChange={() => {
                  setHeadnumberSelectionne(numero.id);
                  setDetailsOuverts(false);
                }}
                label={
                  <span>
                    <strong>{numero.numeroFormate}</strong> ({numero.nom})
                  </span>
                }
              />
            ))}
            {numerosNonConfigures.map((numero) => (
              <Form.Check
                key={numero}
                className="mb-2"
                type="radio"
                id={`headnumber-nonconfigure-${numero}`}
                name="headnumber"
                disabled
                label={
                  <span>
                    <strong>{numero}</strong>{' '}
                    <i
                      className="bi bi-gear-fill text-danger cursor-pointer"
                      title={intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.NON_CONFIGURE'})}
                      onClick={() => onConfigurerNumeroGlobal(numero)}
                    />
                  </span>
                }
              />
            ))}

            {plageActive ? (
              <>
                <div className="mt-4 mb-1">
                  <i className="bi bi-clock text-primary me-2" />
                  <span className="text-primary fw-bold">{plageActive.nomPlage}</span>
                </div>
                <div className="mb-1 fs-6">
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.NUMEROS.PRISE_EN_CHARGE'},
                    {groupe: plageActive.nomGroupe}
                  )}
                  {plageActive.alias && ` — ${plageActive.alias}`}
                </div>
                {groupeActif && (
                  <>
                    <div className="mb-3 fs-6">
                      <i className="bi bi-telephone-fill text-primary me-2" />
                      {intl.formatMessage(
                        {id: 'TELEPHONIE.PBX.MONITEUR.NUMERO_COURT_GROUPE'},
                        {numero: groupeActif.numeroCourt}
                      )}
                    </div>
                    <div className="text-center">
                      <BootstrapButton
                        variant="primary"
                        size="sm"
                        onClick={() => setDetailsOuverts(!detailsOuverts)}
                      >
                        {intl.formatMessage({
                          id: detailsOuverts
                            ? 'TELEPHONIE.PBX.NUMEROS.FERMER'
                            : 'TELEPHONIE.PBX.NUMEROS.DETAILS',
                        })}
                      </BootstrapButton>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-muted fs-7 mt-4">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.AUCUNE_PLAGE_ACTIVE'})}
              </div>
            )}
          </Col>
          <Col xs={12} md={6}>
            {detailsOuverts && groupeActif && (
              <div className="bg-white rounded p-3 border fs-7">
                <div className="fw-bold mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.TITRE'})}
                </div>
                <div className="mb-2">
                  {groupeActif.membres.length === 0
                    ? intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.AUCUN_MEMBRE'})
                    : groupeActif.membres.join(', ')}
                </div>
                <div>
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.DISTRIBUTION'},
                    {
                      label: intl.formatMessage({
                        id: TYPE_DISTRIBUTION_LABEL_ID[groupeActif.typeDistribution],
                      }),
                    }
                  )}
                </div>
                <div>
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.APPELS_REPONDUS'},
                    {
                      statut: intl.formatMessage({
                        id:
                          groupeActif.gestionAppelsRepondus === 'actif'
                            ? 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'
                            : 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF',
                      }),
                    }
                  )}
                </div>
                <div>
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.ANNONCE_ACCUEIL'},
                    {
                      message:
                        groupeActif.messageAccueil ||
                        intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.PAS_DE_MESSAGE'}),
                    }
                  )}
                </div>
                <div>
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.ANNONCE_ATTENTE'},
                    {
                      message:
                        groupeActif.messageAttente ||
                        intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.PAS_DE_MESSAGE'}),
                    }
                  )}
                </div>
                <div>
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.ANNONCE_TRANSFERT'},
                    {
                      message:
                        groupeActif.messageAvantReponse ||
                        intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.PAS_DE_MESSAGE'}),
                    }
                  )}
                </div>
                <div className="mb-2">
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.MUSIQUE_ATTENTE'},
                    {
                      musique:
                        groupeActif.musiqueAttente ||
                        intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.MUSIQUE_DEFAUT'}),
                    }
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span>
                    {intl.formatMessage(
                      {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.COND_NB_MAX'},
                      {n: groupeActif.nbMaxAppelantsFile}
                    )}
                  </span>
                  <i className="bi bi-arrow-right text-primary" />
                  <span>
                    {intl.formatMessage({
                      id: DEVIER_VERS_LABEL_ID[groupeActif.condNbMaxAppelants.devierVers],
                    })}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span>
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.COND_AUCUN_AGENT'})}
                  </span>
                  <i className="bi bi-arrow-right text-primary" />
                  <span>
                    {intl.formatMessage({
                      id: DEVIER_VERS_LABEL_ID[groupeActif.condNbMaxAppelants.devierVers],
                    })}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span>
                    {intl.formatMessage(
                      {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.COND_DUREE_ATTENTE'},
                      {duree: groupeActif.dureeAttenteMax}
                    )}
                  </span>
                  <i className="bi bi-arrow-right text-primary" />
                  <span>
                    {intl.formatMessage({
                      id: DEVIER_VERS_LABEL_ID[groupeActif.condDureeAttenteMax.devierVers],
                    })}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span>
                    {intl.formatMessage(
                      {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.COND_DUREE_SONNERIE'},
                      {duree: groupeActif.dureeSonnerieMax}
                    )}
                  </span>
                  <i className="bi bi-arrow-right text-primary" />
                  <span>
                    {intl.formatMessage({
                      id: DEVIER_VERS_LABEL_ID[groupeActif.condDureeSonnerieMax.devierVers],
                    })}
                  </span>
                </div>

                <div>
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.STATUT'})}{' '}
                  <span className="text-primary fw-bold">
                    {intl.formatMessage({
                      id: groupeActif.actif
                        ? 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'
                        : 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF',
                    })}
                  </span>
                </div>
                <div className="mb-4">
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.REGLE_INACTIF'},
                    {
                      label: intl.formatMessage({
                        id: DEVIER_VERS_LABEL_ID[groupeActif.desactivationDevierVers],
                      }),
                    }
                  )}
                </div>

                <BootstrapButton variant="dark" onClick={() => onEditerGroupe(groupeActif.nom)}>
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.GROUPE.EDITER'})}
                </BootstrapButton>
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const Intro = ({
  onOuvrirEditeur,
  onConfigurerNumeroGlobal,
  onOuvrirCallFlowVisualizer,
  onOuvrirGestionTeams,
  onEditerGroupe,
}: {
  onOuvrirEditeur: () => void;
  onConfigurerNumeroGlobal: (numero?: string) => void;
  onOuvrirCallFlowVisualizer: () => void;
  onOuvrirGestionTeams: () => void;
  onEditerGroupe: (nomGroupe: string) => void;
}): JSX.Element => {
  const intl = useIntl();
  const {data: numerosData} = useGetVirtualPbxNumeros();

  return (
    <div>
      <h2 className="text-primary fw-bold text-uppercase mb-4">
        {intl.formatMessage({id: 'TELEPHONIE.PBX.INTRO.TITRE'})}
      </h2>
      <p className="fs-6 mb-4">{intl.formatMessage({id: 'TELEPHONIE.PBX.INTRO.DESCRIPTION'})}</p>
      <div className="d-flex gap-3 mb-5">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.PBX.INTRO.OUVRIR_EDITEUR'})}
          onClick={onOuvrirEditeur}
        />
        {numerosData && numerosData.numerosGlobaux.length > 0 && (
          <BootstrapButton variant="outline-secondary" onClick={onOuvrirCallFlowVisualizer}>
            {intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.CALL_FLOW_VISUALIZER'})}
          </BootstrapButton>
        )}
      </div>

      <MoniteurActivite
        onConfigurerNumeroGlobal={onConfigurerNumeroGlobal}
        onOuvrirGestionTeams={onOuvrirGestionTeams}
        onEditerGroupe={onEditerGroupe}
      />
    </div>
  );
};

/**
 * MAQUETTE — onglet « Gérer mon virtual PBX » d'une prestation Centrex.
 * Vue d'accueil (description + moniteur d'activité) puis, après « Ouvrir
 * l'éditeur », sous-navigation par menu déroulant (15 sections) ; seule
 * « Numéros » et « IVR/SVI » sont construites pour l'instant, les autres
 * seront ajoutées progressivement.
 */
const VirtualPbx = (): JSX.Element => {
  const intl = useIntl();
  const [vue, setVue] = useState<'intro' | 'editeur' | 'callflow' | 'teams'>('intro');
  const [sousOnglet, setSousOnglet] = useState<PbxSousOnglet>(PBX_SOUS_ONGLETS[0]);
  const [modeConfigNumeroGlobal, setModeConfigNumeroGlobal] = useState(false);
  const [numeroGlobalAConfigurer, setNumeroGlobalAConfigurer] = useState<string | undefined>(
    undefined
  );
  const [ouvrirAjoutGroupeAppel, setOuvrirAjoutGroupeAppel] = useState(false);
  const [ouvrirAjoutGroupePlage, setOuvrirAjoutGroupePlage] = useState(false);
  const [nomGroupeAModifier, setNomGroupeAModifier] = useState<string | undefined>(undefined);

  const dateActuelle = DateTime.now().setLocale('fr').toFormat("cccc d MMMM yyyy, HH'h'mm");

  if (vue === 'callflow') {
    return (
      <div className="mt-4">
        <CallFlowVisualizer
          onFermer={() => setVue('intro')}
          onNaviguerVers={(onglet) => {
            setSousOnglet(
              PBX_SOUS_ONGLETS.find((candidat) => candidat.key === onglet) ?? PBX_SOUS_ONGLETS[0]
            );
            setVue('editeur');
          }}
        />
      </div>
    );
  }

  if (vue === 'teams') {
    return (
      <div className="mt-4">
        <GererTeams onFermer={() => setVue('intro')} />
      </div>
    );
  }

  if (vue === 'intro') {
    return (
      <div className="mt-4">
        <Intro
          onOuvrirEditeur={() => setVue('editeur')}
          onConfigurerNumeroGlobal={(numero) => {
            setNumeroGlobalAConfigurer(numero);
            setModeConfigNumeroGlobal(true);
            setSousOnglet(PBX_SOUS_ONGLETS[0]);
            setVue('editeur');
          }}
          onOuvrirCallFlowVisualizer={() => setVue('callflow')}
          onOuvrirGestionTeams={() => setVue('teams')}
          onEditerGroupe={(nomGroupe) => {
            setNomGroupeAModifier(nomGroupe);
            setSousOnglet(
              PBX_SOUS_ONGLETS.find((onglet) => onglet.key === 'groupes') ?? PBX_SOUS_ONGLETS[0]
            );
            setVue('editeur');
          }}
        />
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-center gap-2">
          <span
            className="rounded-circle"
            style={{width: 8, height: 8, backgroundColor: '#22c55e'}}
          />
          <span className="text-muted fs-7">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.EDITEUR.EN_LIGNE'})} —{' '}
            {dateActuelle.charAt(0).toUpperCase() + dateActuelle.slice(1)}
          </span>
        </div>
        <div className="d-flex gap-3">
          <Button
            label={intl.formatMessage({id: 'COMMON.BACK'})}
            faIcon="bi bi-arrow-left fs-6 me-2"
            onClick={() => setVue('intro')}
          />
        </div>
      </div>

      <Dropdown className="mb-5">
        <Dropdown.Toggle variant="outline-secondary" className="d-inline-flex align-items-center gap-2">
          <i className={`bi ${sousOnglet.icon}`} />
          {intl.formatMessage({id: sousOnglet.labelId})}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{maxHeight: 360, overflowY: 'auto'}}>
          {PBX_SOUS_ONGLETS.map((onglet) => (
            <Dropdown.Item
              key={onglet.key}
              active={onglet.key === sousOnglet.key}
              onClick={() => {
                setSousOnglet(onglet);
                setOuvrirAjoutGroupeAppel(false);
                setOuvrirAjoutGroupePlage(false);
              }}
              className="d-flex align-items-center gap-2"
            >
              <i className={`bi ${onglet.icon}`} />
              {intl.formatMessage({id: onglet.labelId})}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {sousOnglet.key === 'numeros' && (
        <VueNumeros
          modeConfig={modeConfigNumeroGlobal}
          numeroAConfigurer={numeroGlobalAConfigurer}
          onFermerConfig={() => {
            setModeConfigNumeroGlobal(false);
            setNumeroGlobalAConfigurer(undefined);
          }}
          onCreerGroupe={() => {
            setOuvrirAjoutGroupeAppel(true);
            setSousOnglet(
              PBX_SOUS_ONGLETS.find((onglet) => onglet.key === 'groupes') ?? PBX_SOUS_ONGLETS[0]
            );
          }}
          onAjouterGroupePlage={() => {
            setOuvrirAjoutGroupePlage(true);
            setSousOnglet(
              PBX_SOUS_ONGLETS.find((onglet) => onglet.key === 'plages') ?? PBX_SOUS_ONGLETS[0]
            );
          }}
          onOuvrirConfig={() => setModeConfigNumeroGlobal(true)}
        />
      )}
      {sousOnglet.key === 'plages' && (
        <PlagesHoraires
          ouvrirAjoutGroupeInitial={ouvrirAjoutGroupePlage}
          onOuvrirAjoutGroupeConsomme={() => setOuvrirAjoutGroupePlage(false)}
        />
      )}
      {sousOnglet.key === 'groupes' && (
        <Groupes
          ouvrirAjoutInitial={ouvrirAjoutGroupeAppel}
          onOuvrirAjoutConsomme={() => setOuvrirAjoutGroupeAppel(false)}
          nomGroupeAModifierInitial={nomGroupeAModifier}
          onOuvrirModificationConsomme={() => setNomGroupeAModifier(undefined)}
        />
      )}
      {sousOnglet.key === 'bv' && <BoiteVocale />}
      {sousOnglet.key === 'sortants' && <AppelsSortants />}
      {sousOnglet.key === 'melodies' && (
        <div>
          <GestionSons
            type="musique_attente"
            titreId="TELEPHONIE.PBX.SONS.MUSIQUE_ATTENTE.TITLE"
            descriptionId="TELEPHONIE.PBX.SONS.MUSIQUE_ATTENTE.DESCRIPTION"
          />
          <GestionSons
            type="tonalite"
            titreId="TELEPHONIE.PBX.SONS.TONALITE.TITLE"
            descriptionId="TELEPHONIE.PBX.SONS.TONALITE.DESCRIPTION"
          />
        </div>
      )}
      {sousOnglet.key === 'annonces' && (
        <div>
          <GestionSons
            type="annonce_fichier"
            titreId="TELEPHONIE.PBX.SONS.ANNONCE_FICHIER.TITLE"
            descriptionId="TELEPHONIE.PBX.SONS.ANNONCE_FICHIER.DESCRIPTION"
          />
          <GestionSons
            type="tts"
            titreId="TELEPHONIE.PBX.SONS.TTS.TITLE"
            descriptionId="TELEPHONIE.PBX.SONS.TTS.DESCRIPTION"
          />
        </div>
      )}
      {sousOnglet.key === 'ivr' && <ConfigurationSvi />}
      {sousOnglet.key === 'conference' && <ConferenceRoom />}
      {sousOnglet.key === 'historique' && <HistoriqueAppels />}
    </div>
  );
};

export default VirtualPbx;
